import { hierarchy, tree as d3tree, type HierarchyPointNode } from 'd3-hierarchy';
import type { Person, Relationship } from './db';

export interface LayoutNode {
  id: string;
  person: Person;
  x: number;
  y: number;
  depth: number;
  /** True if this person has no parent/child/spouse links (rendered in the
   *  unlinked band below the main tree). */
  orphan: boolean;
}

export interface LayoutLink {
  source: { x: number; y: number };
  target: { x: number; y: number };
  kind: 'parent' | 'spouse';
}

export interface OrphanBand {
  x: number;
  y: number;
  width: number;
  height: number;
  count: number;
}

export interface LayoutResult {
  nodes: LayoutNode[];
  links: LayoutLink[];
  orphanBand: OrphanBand | null;
  width: number;
  height: number;
}

// Root-down ancestor/descendant layout via d3-hierarchy. We accept an
// explicit rootId (falls back to the person with no parents and most descendants).
export function layout(
  people: Person[],
  rels: Relationship[],
  rootId?: string,
  opts: { nodeW?: number; nodeH?: number; gapX?: number; gapY?: number } = {},
): LayoutResult {
  const nodeW = opts.nodeW ?? 180;
  const nodeH = opts.nodeH ?? 72;
  const gapX = opts.gapX ?? 24;
  const gapY = opts.gapY ?? 100;

  if (!people.length) {
    return { nodes: [], links: [], orphanBand: null, width: 0, height: 0 };
  }

  const byId = new Map(people.map((p) => [p.id, p]));
  const childrenOf = new Map<string, string[]>();
  const parentsOf = new Map<string, string[]>();
  const spouseOf = new Set<string>();
  for (const r of rels) {
    if (r.kind === 'parent') {
      const kids = childrenOf.get(r.a) ?? [];
      kids.push(r.b);
      childrenOf.set(r.a, kids);
      const ps = parentsOf.get(r.b) ?? [];
      ps.push(r.a);
      parentsOf.set(r.b, ps);
    } else if (r.kind === 'spouse') {
      spouseOf.add(r.a);
      spouseOf.add(r.b);
    }
  }

  // A person counts as "linked" if they participate in any relationship
  // (parent, child, or spouse). Anyone else is an orphan.
  function isLinked(id: string): boolean {
    return parentsOf.has(id) || childrenOf.has(id) || spouseOf.has(id);
  }

  const root = rootId
    ? byId.get(rootId)
    : [...people]
        .filter((p) => !parentsOf.has(p.id) && isLinked(p.id))
        .sort((a, b) => (childrenOf.get(b.id)?.length ?? 0) - (childrenOf.get(a.id)?.length ?? 0))[0]
      ?? [...people].filter((p) => isLinked(p.id))[0]
      ?? people[0];

  if (!root) return { nodes: [], links: [], orphanBand: null, width: 0, height: 0 };

  type H = { id: string; children: H[] };
  const visited = new Set<string>();
  function build(id: string): H {
    visited.add(id);
    const kids = (childrenOf.get(id) ?? [])
      .filter((k) => byId.has(k) && !visited.has(k))
      .map(build);
    return { id, children: kids };
  }
  const h = hierarchy<H>(build(root.id), (d) => d.children);

  const layoutFn = d3tree<H>().nodeSize([nodeW + gapX, nodeH + gapY]);
  const rooted = layoutFn(h);

  const nodes: LayoutNode[] = [];
  rooted.each((n: HierarchyPointNode<H>) => {
    const p = byId.get(n.data.id);
    if (!p) return;
    nodes.push({ id: n.data.id, person: p, x: n.x, y: n.y, depth: n.depth, orphan: false });
  });

  // Anyone reachable through hierarchy traversal is "main tree" — others fall
  // into the orphan band below.
  const placedInTree = new Set(nodes.map((n) => n.id));

  // First, anyone who IS linked (spouse/parent/child) but didn't get reached
  // by the hierarchy traversal — e.g. a spouse of a leaf, or a sibling tree.
  // Pull them into the main coordinate space adjacent to a connected node so
  // they don't disappear into the orphan band.
  const xs0 = nodes.map((n) => n.x);
  const ys0 = nodes.map((n) => n.y);
  let mainMaxX = xs0.length ? Math.max(...xs0) : 0;
  const mainMaxY = ys0.length ? Math.max(...ys0) : 0;
  for (const p of people) {
    if (placedInTree.has(p.id)) continue;
    if (!isLinked(p.id)) continue;
    // Place to the right of the current main tree's last node, on its top row.
    mainMaxX += nodeW + gapX;
    nodes.push({
      id: p.id, person: p,
      x: mainMaxX, y: 0,
      depth: 0, orphan: false,
    });
    placedInTree.add(p.id);
  }

  // Now compute orphan band — strictly people with NO links at all.
  const orphans = people.filter((p) => !placedInTree.has(p.id));
  let orphanBand: OrphanBand | null = null;
  if (orphans.length) {
    const xs = nodes.map((n) => n.x);
    const ys = nodes.map((n) => n.y);
    const mainMinX = xs.length ? Math.min(...xs) : 0;
    const mainBottom = ys.length ? Math.max(...ys) + nodeH : 0;
    const startY = mainBottom + gapY * 1.5;
    const perRow = Math.max(2, Math.floor(Math.sqrt(orphans.length * 1.6)));
    orphans.forEach((p, i) => {
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      nodes.push({
        id: p.id, person: p,
        x: mainMinX + col * (nodeW + gapX),
        y: startY + row * (nodeH + gapY * 0.6),
        depth: 0,
        orphan: true,
      });
    });

    const rows = Math.ceil(orphans.length / perRow);
    orphanBand = {
      x: mainMinX,
      y: startY,
      width: perRow * (nodeW + gapX) - gapX,
      height: rows * (nodeH + gapY * 0.6) - gapY * 0.6 + nodeH,
      count: orphans.length,
    };
  }

  const posById = new Map(nodes.map((n) => [n.id, n]));
  const links: LayoutLink[] = [];
  for (const r of rels) {
    const a = posById.get(r.a);
    const b = posById.get(r.b);
    if (!a || !b) continue;
    links.push({
      source: { x: a.x, y: a.y },
      target: { x: b.x, y: b.y },
      kind: r.kind,
    });
  }

  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const width = Math.max(...xs) - minX + nodeW;
  const height = Math.max(...ys) - minY + nodeH;

  // Normalise so min is 0,0.
  for (const n of nodes) { n.x -= minX; n.y -= minY; }
  for (const l of links) {
    l.source.x -= minX; l.target.x -= minX;
    l.source.y -= minY; l.target.y -= minY;
  }
  if (orphanBand) {
    orphanBand.x -= minX;
    orphanBand.y -= minY;
  }

  return { nodes, links, orphanBand, width, height };
}
